import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { 
  TextField, 
  Button, 
  Container, 
  Typography, 
  Box, 
  Alert,
  Paper
} from '@mui/material';
import api from '../api/axios';

const ResetPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/, 
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character')
    .required('Required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Required'),
});

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [token, setToken] = useState('');

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError('Invalid reset link. Please request a new password reset.');
    } else {
      setToken(tokenParam);
    }
  }, [searchParams]);

  if (!token) {
    return (
      <Container maxWidth="xs">
        <Box mt={8}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Alert severity="error">
              Invalid reset link. Please request a new password reset.
            </Alert>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => navigate('/forgot-password')}
              sx={{ mt: 2 }}
            >
              Request Password Reset
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xs">
      <Box mt={8}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" align="center" gutterBottom>
            Reset Password
          </Typography>
          <Typography variant="body2" align="center" color="textSecondary" sx={{ mb: 3 }}>
            Enter your new password below.
          </Typography>
          
          {!isSubmitted ? (
            <Formik
              initialValues={{ password: '', confirmPassword: '' }}
              validationSchema={ResetPasswordSchema}
              onSubmit={async (values, { setSubmitting }) => {
                setError('');
                setMessage('');
                try {
                  const res = await api.post('/auth/reset-password', {
                    token,
                    password: values.password
                  });
                  setMessage(res.data.message);
                  setIsSubmitted(true);
                } catch (err) {
                  setError(err.response?.data?.message || 'An error occurred');
                }
                setSubmitting(false);
              }}
            >
              {({ isSubmitting, errors, touched }) => (
                <Form>
                  <Field
                    as={TextField}
                    name="password"
                    label="New Password"
                    type="password"
                    fullWidth
                    margin="normal"
                    error={touched.password && !!errors.password}
                    helperText={touched.password && errors.password}
                  />
                  <Field
                    as={TextField}
                    name="confirmPassword"
                    label="Confirm New Password"
                    type="password"
                    fullWidth
                    margin="normal"
                    error={touched.confirmPassword && !!errors.confirmPassword}
                    helperText={touched.confirmPassword && errors.confirmPassword}
                  />
                  {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={isSubmitting}
                    sx={{ mt: 2 }}
                  >
                    Reset Password
                  </Button>
                </Form>
              )}
            </Formik>
          ) : (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                {message}
              </Alert>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => navigate('/login')}
              >
                Go to Login
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPasswordPage; 